import _ from 'lodash';

const pendingGetRequests = new Map();
const queuedStatisticsRequests = [];
let activeStatisticsRequests = 0;
const MAX_CONCURRENT_STATISTICS_REQUESTS = 4;

function stableRequestKey(value) {
    if (_.isArray(value)) {
        return value.map(stableRequestKey);
    }

    if (_.isPlainObject(value)) {
        return _.chain(value)
            .keys()
            .sort()
            .reduce((result, key) => ({
                ...result,
                [key]: stableRequestKey(value[key]),
            }), {})
            .value();
    }

    return value;
}

function isStatisticsRequest(path) {
    return _.includes(path, '/statistics/');
}

function isMemberPortalAccessDenied(page, code, err) {
    const responseCode = Number(code || _.get(err, 'status'))

    return responseCode === 403 && (
        _.startsWith(page, '/my-benefit')
        || page === '/auto-login'
    )
}

function scheduleStatisticsRequest(task) {
    return new Promise((resolve, reject) => {
        const run = async () => {
            activeStatisticsRequests += 1;

            try {
                resolve(await task());
            } catch (err) {
                reject(err);
            } finally {
                activeStatisticsRequests -= 1;

                const next = queuedStatisticsRequests.shift();
                if (next) {
                    next();
                }
            }
        };

        if (activeStatisticsRequests < MAX_CONCURRENT_STATISTICS_REQUESTS) {
            run();
        } else {
            queuedStatisticsRequests.push(run);
        }
    });
}

export function urlRequests(object) {
    let request;
    _.forEach(object, (item, key) => {
        if (_.isArray(item)) {
            _.forEach(item, (val) => {
                request = _.concat(request, `${key}[]=${val}`);
            });
        } else {
            request = _.concat(request, `${key}=${item}`);
        }
    });
    return _.chain(request).compact().join('&').value();
}

import * as Sentry from "@sentry/browser";
export async function fetchRequest(path, method, request = {}, fullMessages = false, with_code = false) {
    const otp_verified_token = localStorage.getItem('otp_verified_token');
    const slug = localStorage.getItem('slug');
    const token = localStorage.getItem('access_token');

    const route = useRoute()
    const page = route.path

    const { backendUrl: envURL } = useAppConfig();

    let headers = _.eq(method, 'form') ? {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data'
    } : {
        'Authorization': token ? `Bearer ${token}` : ''
    }

    if(slug){
        headers = {
            ...headers,
            'X-Distributor-Slug': slug,
        }
    }

    if(otp_verified_token){
        headers = {
            ...headers,
            'X-OTP-Verified-Token': otp_verified_token,
        }
    }


    try {
        return await $fetch(`${envURL}/${path}`, {
            method: _.eq(method, 'form') ? 'post' : method,
            body: request,
            headers
        })
    } catch (err) {
        const code = _.chain(err.data).get('code').value()
        const message = _.chain(err.data).get('message').value()

        if (!_.includes(['/login', '/token-expired'], page)) {
            if (isMemberPortalAccessDenied(page, code, err)) {
                const redirectKey = 'mixcare:redirecting-portal-access-denied'
                if (!sessionStorage.getItem(redirectKey)) {
                    sessionStorage.setItem(redirectKey, '1')
                    window.location.replace('/token-expired?reason=portal-access')
                }

                return
            }

            if (_.includes([401], code) && _.includes(['Unauthenticated.'], message)) {
                const redirectKey = 'mixcare:redirecting-token-expired'
                if (!sessionStorage.getItem(redirectKey)) {
                    sessionStorage.setItem(redirectKey, '1')
                    window.location.replace('/token-expired')
                }
                return
            }
        }
        if (!_.includes([401], code) || !_.includes(['Unauthenticated.'], message)) {
            Sentry.captureException(err);
        }
        if (with_code) {
            return {
                code,
                status: err.status,
                message: _.get(err, 'data.message')
            }
        }
        if (fullMessages) {
            return _.chain(err).get('data.data').mapValues(a => a.message).toArray().join(', ').value()
        } else {
            return _.get(err, 'data.message');
        }
    }
}

export async function requestWithArrayBuffer(path, method, file_name, fullMessages = false) {
    const token = localStorage.getItem('access_token');
    const { backendUrl: envURL } = useAppConfig();
    const slug = localStorage.getItem('slug')
    try {
        const res = await $fetch(`${envURL}/${path}`, {
            method,
            responseType: 'blob',
            headers: {
                'X-Distributor-Slug': slug,
                'Authorization': token ? `Bearer ${token}` : ''
            }
        }).then((content) => {
            const a = document.createElement("a");
            const url = window.URL.createObjectURL(content);
            a.href = url;
            a.download = file_name;
            a.click();
        }).catch((err) => {
            const code = _.chain(err.message).split(' ').compact().last().value()
            return code
        })
        return res
    } catch (err) {
        // const code = _.chain(err.data).get('code').value()
        // const message = _.chain(err.data).get('message').value()
        // if (_.includes([401], code) && _.includes(['Unauthenticated.'], message)) {
        //     window.location.replace('/token-expired')
        //     return
        // }
        // if (fullMessages)
        //     return _.chain(err.data.data).mapValues(a => a.message).toArray().join(', ').value()
        // return _.get(err, 'data.message');
    }
}

export async function postFormDataRequest(path, request, fullMessages = false, with_code = false) {
    return await fetchRequest(path, 'form', request, fullMessages, with_code)
}

export async function getRequest(path, request = {}, fullMessages = false, with_code = false) {
    const body = {
        _method: 'GET',
        ...request
    };

    if (!isStatisticsRequest(path)) {
        return await fetchRequest(path, 'post', body, fullMessages, with_code)
    }

    const requestKey = JSON.stringify({
        path,
        body: stableRequestKey(body),
        fullMessages,
        with_code,
    });

    if (pendingGetRequests.has(requestKey)) {
        return await pendingGetRequests.get(requestKey);
    }

    const promise = scheduleStatisticsRequest(() => fetchRequest(path, 'post', body, fullMessages, with_code))
        .finally(() => {
            pendingGetRequests.delete(requestKey);
        });

    pendingGetRequests.set(requestKey, promise);

    return await promise;
}

export async function postRequest(path, request, fullMessages = false, with_code = false) {
    return await fetchRequest(path, 'post', request, fullMessages, with_code)
}

export async function putRequest(path, request, fullMessages = false, with_code = false) {
    return await fetchRequest(path, 'put', request, fullMessages, with_code)
}

export async function deleteRequest(path, request, fullMessages = false, with_code = false) {
    return await fetchRequest(path, 'delete', request, fullMessages, with_code)
}

export async function patchRequest(path, request, fullMessages = false, with_code = false) {
    return await fetchRequest(path, 'patch', request, fullMessages, with_code)
}